import angular from "angular";
import { Ng1StateDeclaration, StateService, Transition } from "angular-ui-router";
import { configureStates, resolveFromParam, RouteDeclaration } from "../../core/router/router-helper.provider";
import { EmployeeService } from "../../core/services";
import { Resolves as EditTimeEntryModalResolves } from "./edit/edit-time-entry-modal.component";

const editTimeEntryConfig = ({ name, url, isEmployerView, title }): Partial<Ng1StateDeclaration> => ({
    name,
    url,
    params: {
        // To prevent state changes from navigating from reviewTimeCards (no employee) to the employee being edited,
        // introduce a second parameter. They will be coalesced into one value for the value resolution.
        editEmployeeID: { type: "int", value: null },
        employeeName: { type: "string", value: null },
        timeEntryID: { type: "int", value: null }
    },
    resolve: {
        employeeName: resolveFromParam("employeeName"),
        timeEntryID: resolveFromParam("timeEntryID"),
        employeeID: ($transition$: Transition, employeeService: EmployeeService) => {
            'ngInject';
            const identity = employeeService.retrieveIdentity();
            const { employeeID, editEmployeeID } = $transition$.params();
            return identity.then(identity => {
                return editEmployeeID ?? employeeID ?? identity.EmployeeID;
            })
        }
    },
    data: {
        title: title
    },
    onEnter: ($uibModal, $state: StateService, $transition$: Transition, timeEntryID: number,
        employeeID: number, employeeName: string) => {
        'ngInject';
        // TODO: Deny access if the requested period has been submitted.
        const params = $transition$.params();
        const resolves: EditTimeEntryModalResolves = {
            entryData: {
                employeeId: employeeID,
                employeeName,
                // startDate and endDate are dynamic parameters, so get them from transition to avoid injecting stale data.
                startDate: params.startDate,
                endDate: params.endDate,
                timeEntryID
            },
            isEmployerView
        };
        const modal = $uibModal.open({
            component: "apexEditTimeEntryModal",
            windowClass: 'xx-dialog pagespinner',
            size: 'lg',
            backdrop: 'static',
            resolve: resolves
        });
        modal.result
            .then(() => {
                $state.go("^", null, { reload: true });
            })
            .catch(ex => {
                if (!ex) {
                    // Dismissed by some other source/$uibModalStack.dismissAll().
                    return;
                }
                if (ex !== "cancel") {
                    console.error(ex);
                }
                return $state.go("^");
            });
    },
    onExit: $uibModalStack => {
        'ngInject';
        // Handle back button events.
        $uibModalStack.dismissAll();
    }
});

const routes: RouteDeclaration[] = [{
    state: "timeEntry",
    config: {
        url: "/time-entry",
        abstract: true
    }
}, {
    state: "timeEntry.reviewPayPeriods",
    config: {
        url: "^/TimeEntry/ReviewPayPeriods?{startDate}&endDate&currDate&{icdCorporationID}",
        component: "apexListTimeCardsContainer",
        params: {
            startDate: { type: "date", dynamic: true },
            endDate: { type: "date", dynamic: true },
            // currDate is an alias for endDate
            currDate: { type: "date", dynamic: true },
            icdCorporationID: { type: "int", dynamic: true }
        },
        resolve: {
            startDate: resolveFromParam("startDate"),
            endDate: ($transition$: Transition) => {
                'ngInject';
                const { currDate, endDate } = $transition$.params();
                return endDate || currDate;
            },
            icdCorporationID: resolveFromParam("icdCorporationID")
        },
        data: {
            pageHeader: "Approve Time Cards"
        }
    }
},
{
    state: "timeEntry.enterTime",
    config: {
        url: "^/TimeEntry/EnterTime?startDate&endDate&icdCorporationID",
        params: {
            startDate: { type: "date", dynamic: true },
            endDate: { type: "date", dynamic: true },
            icdCorporationID: { type: "int", dynamic: true }
        },
        component: "apexListTimeEntriesContainer",
        resolve: {
            startDate: resolveFromParam("startDate"),
            endDate: ($transition$: Transition) => {
                'ngInject';
                const { currDate, endDate } = $transition$.params();
                return endDate || currDate;
            },
            icdCorporationID: resolveFromParam("icdCorporationID"),
            // This uses the same component as ReviewTime, but disabling the review features
            // and always displaying information for the logged in user.
            employeeID: (employeeService: EmployeeService) => {
                'ngInject';
                return employeeService.retrieveIdentity().then(e => e.EmployeeID);
            },
            isReview: () => false
        },
        data: {
            title: "Log My Hours"
        }
    }
}, {
    state: "timeEntry.reviewTime",
    config: {
        url: "^/TimeEntry/ReviewTime?startDate&endDate&currDate&icdCorporationID&employeeID&unapprovedOnly",
        params: {
            startDate: { type: "date", dynamic: true },
            endDate: { type: "date", dynamic: true },
            // currDate is a synonym for endDate; keep around to avoid breaking existing URLs
            currDate: { type: "date", dynamic: true },
            icdCorporationID: { type: "int", dynamic: true },
            employeeID: { type: "int", dynamic: true },
            unapprovedOnly: { type: "bool" }
        },
        component: "apexListTimeEntriesContainer",
        resolve: {
            startDate: resolveFromParam("startDate"),
            endDate: ($transition$: Transition) => {
                'ngInject';
                const { currDate, endDate } = $transition$.params();
                return endDate || currDate;
            },
            icdCorporationID: resolveFromParam("icdCorporationID"),
            employeeID: resolveFromParam("employeeID"),
            isReview: () => true,
            unapprovedOnly: resolveFromParam("unapprovedOnly")
        },
        data: {
            title: "Time Card Review"
        }
    }
}, {
    state: "timeEntry.reviewTime.new",
    config: editTimeEntryConfig({
        name: "timeEntry.reviewTime.new",
        url: "/new?employeeName&editEmployeeID",
        isEmployerView: true,
        title: "New Time Entry"
    })
}, {
    state: "timeEntry.reviewTime.edit",
    config: editTimeEntryConfig({
        name: "timeEntry.reviewTime.edit",
        url: "/edit?employeeName&timeEntryID&editEmployeeID",
        isEmployerView: true,
        title: "Edit Time Entry"
    })
}, {
    state: "timeEntry.enterTime.new",
    config: editTimeEntryConfig({
        name: "timeEntry.enterTime.new",
        url: "/new?employeeName",
        isEmployerView: false,
        title: "Edit Time Entry"
    })
}, {
    state: "timeEntry.enterTime.edit",
    config: editTimeEntryConfig({
        name: "timeEntry.enterTime.edit",
        url: "/edit?employeeName&timeEntryID",
        isEmployerView: false,
        title: "Edit Time Entry"
    })
}];

export const TimeEntryRoutingModule = angular
    .module("apex.mainApp.timeEntry.routing", [])
    .run(configureStates(routes))
    .name;
